"use client";

import { useEffect, useState } from "react";
import { api } from "@tillix/tx-core";
import { useGlobal } from "@tillix/tx-core/react";
import { isValidRoutingNumber } from "@tillix/utils";
import { reaction } from "mobx";
import { useWatch } from "react-hook-form";

const DEBOUNCE_MS = 150;

export interface BankLookupResult {
  name: string;
  svg: string | null;
  routingNumbers: string[];
}

export interface UseBankByRoutingNumberReturn {
  bank: BankLookupResult | null;
  isLoading: boolean;
}

/**
 * Subscribes to a routing-number form field, debounces input by 150ms, and
 * resolves a matching bank with a store-first lookup strategy:
 *
 *   1. Check `globalStore.banks` for a cached bank whose `routingNumbers`
 *      includes the typed RN — instant hit, no network call.
 *   2. On a store miss, call `api.static.bank.byRoutingNumber`.
 *   3. On a fetch hit, write the result back into `globalStore.banks` so
 *      future lookups for any of the bank's RNs are served from the cache.
 *   4. On fetch miss / abort / error, silently report `bank: null` (no toast,
 *      no form-level error).
 *
 * Subscribes reactively to `globalStore.banks` so a store change (e.g.
 * `useHydrateBanks` finishing in another component) re-runs the cache check.
 */
export function useBankByRoutingNumber(
  fieldName = "routingNumber",
): UseBankByRoutingNumberReturn {
  const watched = useWatch({ name: fieldName }) as string | undefined;
  const routingNumber = (watched ?? "").trim();
  const globalStore = useGlobal();
  // Mirror `globalStore.banks` into React state via a MobX `reaction` so the
  // store's observable updates trigger a re-render even when this hook is
  // consumed by a component that isn't wrapped in `observer`.
  const [cachedBanks, setCachedBanks] = useState(() => globalStore.banks);
  useEffect(
    () => reaction(() => globalStore.banks, setCachedBanks),
    [globalStore],
  );
  const [bank, setBank] = useState<BankLookupResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setBank(null);

    if (routingNumber.length !== 9 || !isValidRoutingNumber(routingNumber)) {
      setIsLoading(false);
      return;
    }

    // Store-first lookup
    const cached = Object.values(cachedBanks ?? {}).find((b) =>
      b.routingNumbers.includes(routingNumber),
    );
    if (cached) {
      setBank({
        name: cached.name,
        svg: cached.svg,
        routingNumbers: [...cached.routingNumbers],
      });
      setIsLoading(false);
      return;
    }

    // Fetch fallback (debounced)
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const result = await api.static.bank.byRoutingNumber(
          { routingNumber },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;

        if (result) {
          // Writeback so subsequent lookups for any of this bank's routing
          // numbers hit the store cache.
          globalStore.cacheBank(result);
          setBank(result);
        } else {
          setBank(null);
        }
        setIsLoading(false);
      } catch {
        if (!controller.signal.aborted) {
          setBank(null);
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [routingNumber, globalStore, cachedBanks]);

  return { bank, isLoading };
}
